// ============================================================================
// CHANGO V5.0 — D5.2 useUndoRedo
// ============================================================================
// A form-state history hook. Keeps a bounded ring buffer of snapshots and
// exposes undo / redo operations plus the keyboard bindings for Cmd/Ctrl+Z
// and Cmd/Ctrl+Shift+Z (mac) or Ctrl+Y (win/linux).
//
// Design decisions:
//
//   * **Coalescing window** — rapid consecutive `setState` calls within
//     `coalesceMs` (default 600 ms) are folded into the most recent history
//     entry. Without this, typing "hello" into an input would consume 5
//     undo steps; with it, holding still for 600 ms "commits" the current
//     state as a checkpoint.
//   * **Bounded capacity** — `capacity` (default 50) protects memory. When
//     the buffer is full, the oldest snapshot is dropped FIFO.
//   * **Structural comparison is caller-side** — the hook only records a
//     new snapshot when the reducer result differs by reference. Callers
//     that want deep-equality dedup should pass in immutable updates or
//     memoize their state themselves.
//   * **Redo branch pruning** — the moment a new edit lands while some
//     redos remain queued, the redo tail is dropped. This mirrors the
//     behavior of every editor on the planet.
//   * **Keyboard bridge** — `bindShortcuts(target)` returns a cleanup
//     function; pass `window` (or a specific element) and wire it inside
//     a useEffect.
//
// The state machine is a pure reducer so it's unit-testable without a
// React renderer. See `hooks/__tests__/useUndoRedo.test.js`.
// ============================================================================

import { useCallback, useEffect, useMemo, useReducer } from 'react';

export const DEFAULT_CAPACITY = 50;
export const DEFAULT_COALESCE_MS = 600;

// ---------------------------------------------------------------------------
// Pure reducer — exported for tests.
// ---------------------------------------------------------------------------
//
// State shape:
//   { past: T[], present: T, future: T[], lastWrite: number }
//
// Actions:
//   { type: 'set', updater: T | ((prev: T) => T), commit?: boolean, now?: number }
//   { type: 'undo' }
//   { type: 'redo' }
//   { type: 'reset', value: T }
//
// `now` on the 'set' action is injectable so tests can control the clock
// without mocking Date.now globally.
export function undoRedoReducer(state, action, { capacity = DEFAULT_CAPACITY, coalesceMs = DEFAULT_COALESCE_MS } = {}) {
  switch (action.type) {
    case 'set': {
      const nextPresent = typeof action.updater === 'function'
        ? action.updater(state.present)
        : action.updater;
      if (nextPresent === state.present) return state;

      const now = typeof action.now === 'number' ? action.now : Date.now();
      const shouldCoalesce =
        !action.commit
        && state.past.length > 0
        && state.lastWrite > 0
        && (now - state.lastWrite) < coalesceMs;

      const nextPast = shouldCoalesce
        ? state.past
        : [...state.past, state.present].slice(-capacity);

      return {
        past: nextPast,
        present: nextPresent,
        future: [],      // redo tail always pruned on fresh edit
        lastWrite: now,
      };
    }
    case 'undo': {
      if (state.past.length === 0) return state;
      const newPresent = state.past[state.past.length - 1];
      const newPast = state.past.slice(0, -1);
      return {
        past: newPast,
        present: newPresent,
        future: [state.present, ...state.future].slice(0, capacity),
        lastWrite: 0,  // any subsequent 'set' is a fresh step, not a coalesce
      };
    }
    case 'redo': {
      if (state.future.length === 0) return state;
      const [next, ...rest] = state.future;
      return {
        past: [...state.past, state.present].slice(-capacity),
        present: next,
        future: rest,
        lastWrite: 0,
      };
    }
    case 'reset': {
      return {
        past: [],
        present: action.value,
        future: [],
        lastWrite: 0,
      };
    }
    default:
      return state;
  }
}

export function initialUndoRedoState(value) {
  return { past: [], present: value, future: [], lastWrite: 0 };
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

export function useUndoRedo(initialState, options = {}) {
  const {
    capacity = DEFAULT_CAPACITY,
    coalesceMs = DEFAULT_COALESCE_MS,
  } = options;

  // useReducer gives us stable `dispatch`. The options (capacity/coalesceMs)
  // are closed over via a wrapper reducer so downstream reducer stays pure.
  const wrappedReducer = useMemo(
    () => (s, a) => undoRedoReducer(s, a, { capacity, coalesceMs }),
    [capacity, coalesceMs],
  );
  const [history, dispatch] = useReducer(wrappedReducer, initialState, initialUndoRedoState);

  const setState = useCallback((updater, opts = {}) => {
    dispatch({ type: 'set', updater, commit: !!opts.commit });
  }, []);
  const undo = useCallback(() => { dispatch({ type: 'undo' }); }, []);
  const redo = useCallback(() => { dispatch({ type: 'redo' }); }, []);
  const reset = useCallback((value) => { dispatch({ type: 'reset', value }); }, []);

  const canUndo = history.past.length > 0;
  const canRedo = history.future.length > 0;

  // Keyboard bridge — returns a cleanup function. Caller wraps in useEffect
  // so React manages install/uninstall.
  const bindShortcuts = useCallback((target) => {
    const t = target || (typeof window !== 'undefined' ? window : null);
    if (!t) return () => {};
    const onKeyDown = (ev) => {
      // Respect IME composition — never undo mid-CJK-input.
      if (ev.isComposing) return;
      const meta = ev.metaKey || ev.ctrlKey;
      if (!meta) return;
      const key = (ev.key || '').toLowerCase();
      if (key === 'z' && !ev.shiftKey) {
        ev.preventDefault();
        dispatch({ type: 'undo' });
        return;
      }
      if ((key === 'z' && ev.shiftKey) || key === 'y') {
        ev.preventDefault();
        dispatch({ type: 'redo' });
        return;
      }
    };
    t.addEventListener('keydown', onKeyDown);
    return () => t.removeEventListener('keydown', onKeyDown);
  }, []);

  return useMemo(() => ({
    state: history.present,
    setState,
    undo,
    redo,
    reset,
    canUndo,
    canRedo,
    historySize: history.past.length,
    redoSize: history.future.length,
    bindShortcuts,
  }), [history.present, history.past.length, history.future.length,
       setState, undo, redo, reset, canUndo, canRedo, bindShortcuts]);
}

/**
 * Convenience side-effect hook: attach undo/redo shortcuts to `window` for
 * the lifetime of the calling component. Pass the object returned by
 * `useUndoRedo(...)`.
 */
export function useUndoRedoShortcuts(controller) {
  useEffect(() => {
    if (!controller || typeof controller.bindShortcuts !== 'function') return undefined;
    return controller.bindShortcuts();
  }, [controller]);
}
